import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Menu,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Divider,
  Badge,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  Button
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Send as SendIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { fetchNotifications, markAllNotificationsAsRead } from '../../redux/slices/notificationsSlice';
import { fetchSentMessages } from '../../redux/slices/driverMessagesSlice';
import brandingConfig from '../../config/brandingConfig';
import SendMessageDialog from './SendMessageDialog';

// Animations
const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const blink = keyframes`
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0.3;
  }
`;

const StyledBadge = styled(Badge)(({ theme, $isOverdue }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: $isOverdue ? brandingConfig.colors.error : brandingConfig.colors.primary,
    color: 'white',
    animation: $isOverdue ? `${blink} 1s infinite` : 'none',
  },
}));

const PulsingButton = styled(IconButton)(({ theme, $isOverdue }) => ({
  animation: $isOverdue ? `${pulse} 2s infinite` : 'none',
}));

const NotificationCenter = ({ user, onSendMessage }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { notifications, unreadCount, overdueRidesCount } = useSelector((state) => state.notifications);
  const { sentMessages, messageHistory } = useSelector((state) => state.driverMessages);
  
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  useEffect(() => {
    if (user?.uid) {
      dispatch(fetchNotifications({ userId: user.uid }));
      dispatch(fetchSentMessages({ limitCount: 10 }));
    }
  }, [user?.uid, dispatch]);

  const formatNotificationDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Gerade eben';
      if (diffInMinutes < 60) return `vor ${diffInMinutes} Min`;
      if (diffInMinutes < 1440) return `vor ${Math.floor(diffInMinutes / 60)} Std`;
      
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.data?.rideId) {
      navigate(`/fahrten/${notification.data.rideId}`);
    }
  };

  const handleSendMessage = () => {
    setSendDialogOpen(true);
    if (onSendMessage) onSendMessage();
  };

  const getNotificationIcon = (type, priority) => {
    switch (type) {
      case 'overdue_ride':
        return <WarningIcon sx={{ color: brandingConfig.colors.error }} />;
      case 'central_message':
        return priority === 'high' ? 
          <WarningIcon sx={{ color: brandingConfig.colors.warning }} /> :
          <InfoIcon sx={{ color: brandingConfig.colors.info }} />;
      default:
        return <InfoIcon sx={{ color: brandingConfig.colors.info }} />;
    }
  };

  const overdueNotifications = notifications?.filter(n => n.type === 'overdue_ride' && !n.is_read) || [];
  const regularNotifications = notifications?.filter(n => n.type !== 'overdue_ride') || [];
  const recentSentMessages = sentMessages?.slice(0, 3) || [];

  const totalBadgeCount = overdueRidesCount + unreadCount;

  return (
    <>
      {/* Header */}
      <Box key="header" sx={{ p: 2, backgroundColor: brandingConfig.colors.primary, color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon />
            Benachrichtigungen
            {totalBadgeCount > 0 && (
              <Chip 
                label={totalBadgeCount} 
                size="small" 
                sx={{ 
                  backgroundColor: 'white', 
                  color: brandingConfig.colors.primary,
                  fontWeight: 'bold'
                }} 
              />
            )}
          </Typography>
          
          <Tooltip title="Nachricht senden">
            <IconButton 
              size="small" 
              onClick={handleSendMessage}
              sx={{ color: 'white' }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Quick Send Button */}
      <MenuItem 
        key="quick-send"
        onClick={handleSendMessage}
        sx={{ 
          backgroundColor: brandingConfig.colors.background,
          borderBottom: `1px solid ${brandingConfig.colors.divider}`,
          '&:hover': {
            backgroundColor: brandingConfig.colors.primaryLight + '20'
          }
        }}
      >
        <ListItemIcon>
          <SendIcon sx={{ color: brandingConfig.colors.primary }} />
        </ListItemIcon>
        <ListItemText 
          primary="Nachricht an Fahrer senden"
          secondary="Neue Mitteilung oder Broadcast erstellen"
        />
      </MenuItem>

      <Divider key="divider-main" />

      {/* Overdue Rides Section */}
      {overdueNotifications.length > 0 && (
        <>
          <Box key="overdue-header" sx={{ p: 1, backgroundColor: brandingConfig.colors.error + '10' }}>
            <Typography variant="subtitle2" sx={{ color: brandingConfig.colors.error, fontWeight: 'bold' }}>
              🚨 Überfällige Fahrten ({overdueNotifications.length})
            </Typography>
          </Box>
          
          {overdueNotifications.slice(0, 3).map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                borderLeft: `4px solid ${brandingConfig.colors.error}`,
                backgroundColor: brandingConfig.colors.error + '05',
                '&:hover': {
                  backgroundColor: brandingConfig.colors.error + '15'
                }
              }}
            >
              <ListItemIcon>
                {getNotificationIcon(notification.type, notification.data?.priority)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: brandingConfig.colors.error }}>
                    ⚠️ {notification.title}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" sx={{ color: 'text.primary', display: 'block' }}>
                      {notification.message}
                    </Typography>
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {formatNotificationDate(notification.created_at)}
                    </Typography>
                  </>
                }
              />
            </MenuItem>
          ))}
          
          <Divider key="overdue-divider" />
        </>
      )}

      {/* Recent Sent Messages Section */}
      {recentSentMessages.length > 0 && (
        <>
          <Box key="sent-header" sx={{ p: 1, backgroundColor: brandingConfig.colors.primary + '10' }}>
            <Typography variant="subtitle2" sx={{ color: brandingConfig.colors.primary, fontWeight: 'bold' }}>
              📤 Kürzlich gesendet
            </Typography>
          </Box>
          
          {recentSentMessages.map((message) => (
            <MenuItem key={message.id} disabled>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: brandingConfig.colors.success }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      {message.title}
                    </Typography>
                    {message.recipientId === 'all' && (
                      <Chip label="Broadcast" size="small" color="primary" />
                    )}
                    <Chip 
                      label={message.priority === 'high' ? 'Hoch' : 'Normal'} 
                      size="small" 
                      color={message.priority === 'high' ? 'warning' : 'default'} 
                    />
                  </Box>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" sx={{ fontSize: '0.8rem', display: 'block' }}>
                      {message.message?.length > 50 ? `${message.message.substring(0, 50)}...` : message.message}
                    </Typography>
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {formatNotificationDate(message.createdAt)}
                    </Typography>
                  </>
                }
              />
            </MenuItem>
          ))}
          
          <Divider key="sent-divider" />
        </>
      )}

      {/* Regular Notifications Section */}
      {regularNotifications.length > 0 ? (
        <>
          {overdueNotifications.length > 0 && (
            <Box key="regular-header" sx={{ p: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Weitere Benachrichtigungen
              </Typography>
            </Box>
          )}
          
          {regularNotifications.slice(0, 4).map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                borderLeft: notification.is_read ? 'none' : `4px solid ${brandingConfig.colors.primary}`
              }}
            >
              <ListItemIcon>
                {getNotificationIcon(notification.type, notification.data?.priority)}
              </ListItemIcon>
              <ListItemText
                primary={notification.title}
                secondary={
                  <>
                    <Typography component="span" variant="body2" sx={{ display: 'block' }}>
                      {notification.message}
                    </Typography>
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {formatNotificationDate(notification.created_at)}
                    </Typography>
                  </>
                }
              />
            </MenuItem>
          ))}
        </>
      ) : overdueNotifications.length === 0 && recentSentMessages.length === 0 ? (
        <MenuItem key="no-notifications" disabled>
          <ListItemText 
            primary="Keine Benachrichtigungen"
            secondary="Alle Benachrichtigungen sind gelesen"
          />
        </MenuItem>
      ) : null}

      {/* Footer */}
      {(notifications?.length > 7 || messageHistory?.length > 3) && (
        <>
          <Divider key="footer-divider" />
          <MenuItem key="all-notifications">
            <ListItemIcon>
              <VisibilityIcon />
            </ListItemIcon>
            <ListItemText 
              primary={`Alle ${notifications?.length || 0} Benachrichtigungen anzeigen`}
            />
          </MenuItem>
        </>
      )}

      <SendMessageDialog 
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
      />
    </>
  );
};

export default NotificationCenter;